import React, { useState, useEffect, useMemo } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "./Logo";
import { api } from "../lib/api";

const ALL_NAV_ITEMS = [
  { to: "/accueil", label: "Accueil" },
  { to: "/horaire", label: "Horaire" },
  { to: "/equipes", label: "Équipes" },
  { to: "/classement", label: "Classement" },
  { to: "/pointeurs", label: "Pointeurs", requiresFlag: "stats_public" },
  { to: "/galerie", label: "Galerie" },
  { to: "/nouvelles", label: "Nouvelles" },
  { to: "/commanditaires", label: "Partenaires" },
  { to: "/reglements", label: "Règlements" },
  { to: "/infos", label: "Infos" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [flags, setFlags] = useState({ stats_public: false });
  const location = useLocation();

  useEffect(() => {
    api.get("/settings").then((r) => setFlags(r.data)).catch(() => {});
  }, [location.pathname]);

  const NAV_ITEMS = useMemo(
    () => ALL_NAV_ITEMS.filter((it) => !it.requiresFlag || flags[it.requiresFlag]),
    [flags]
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/accueil" className="flex items-center gap-3 group" data-testid="nav-logo">
          <Logo className="h-12 w-12 group-hover:scale-105 transition-transform" />
          <div className="leading-none hidden sm:block">
            <div className="font-display text-2xl tracking-wider text-white">COUPE CH</div>
            <div className="text-[10px] tracking-[0.3em] text-white/60 uppercase">Édition 2026</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm uppercase tracking-[0.18em] font-condensed font-semibold transition-colors ${
                  isActive ? "text-white" : "text-white/70 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-active"
                      className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-[#E60012]"
                      transition={{ type: "spring", stiffness: 400, damping: 35 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/horaire"
          data-testid="nav-cta-horaire"
          className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.15em] text-sm transition-all hover:-translate-y-0.5 clip-tab"
        >
          Horaire complet
        </Link>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-burger"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `py-3 border-b border-white/5 font-condensed uppercase tracking-[0.18em] text-sm ${
                      isActive ? "text-[#E60012]" : "text-white/80"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
